/** Restored selectors have no known page pairing, so their `path` and `domain` are null. */
export type ElementPick = { selector: string; path: string | null; domain: string | null }
export type PlacementPosition = 'before' | 'after' | 'inside'
export type PlacementPick = ElementPick & { position: PlacementPosition }
export type PickerPick = ElementPick | PlacementPick
export type PlacementOptions = { label: string; position: PlacementPosition }

/** What the page knows about a pick it can find: a plain-language label, a text snippet and its nearest meaningful container. */
export type PickDetail = ElementPick & {
  label: string
  text: string
  parentSelector: string | null
  matchCount?: number
  canInsertInside?: boolean
}

export type PickerMessage =
  | { type: 'rm-picker:ready' }
  | {
      type: 'rm-picker:init'
      restrictTo?: string
      multiple: boolean
      picks: PickerPick[]
      browse: boolean
      /** Rendered size of one page pixel in the dashboard, so labels on the page stay legible when the frame is scaled. */
      scale?: number
      placement?: PlacementOptions
    }
  | ({
      type: 'rm-picker:pick'
      path: string
      domain: string
      position?: PlacementPosition
      canInsertInside?: boolean
    } & ElementPick)
  | { type: 'rm-picker:position'; position: PlacementPosition }
  | { type: 'rm-picker:ghost'; canInsertInside: boolean }
  | { type: 'rm-picker:unpick'; selector: string; path: string | null; domain: string | null }
  | { type: 'rm-picker:navigated'; url: string; path: string }
  | { type: 'rm-picker:navigate'; url: string }
  | { type: 'rm-picker:mode'; browse: boolean }
  | { type: 'rm-picker:details'; picks: PickDetail[] }
  | { type: 'rm-picker:focus'; pick: ElementPick | null; scroll: boolean }
  | { type: 'rm-picker:alt'; held: boolean }
  | { type: 'rm-picker:clear-hover' }
  | { type: 'rm-picker:loading' }

function isPick(value: unknown): value is ElementPick {
  return (
    typeof value === 'object' &&
    value !== null &&
    'selector' in value &&
    typeof value.selector === 'string' &&
    value.selector.length > 0 &&
    'path' in value &&
    (typeof value.path === 'string' || value.path === null) &&
    'domain' in value &&
    (typeof value.domain === 'string' || value.domain === null) &&
    (!('position' in value) || isPosition(value.position))
  )
}

export function samePick(a: ElementPick, b: ElementPick): boolean {
  return a.selector === b.selector && a.path === b.path && a.domain === b.domain
}

export function pickKey(pick: ElementPick): string {
  return JSON.stringify([pick.domain, pick.path, pick.selector])
}

export function isPickOnPage(pick: ElementPick, path: string, domain: string): boolean {
  return (pick.path === null || pick.path === path) && (pick.domain === null || pick.domain === domain)
}

export function replacePlacementPick(picks: readonly PickerPick[], pick: PickerPick): PickerPick[] {
  const index = picks.findIndex((existing) => existing.path === pick.path && existing.domain === pick.domain)
  return index < 0 ? [...picks, pick] : picks.map((existing, at) => (at === index ? pick : existing))
}

function isPosition(value: unknown): value is PlacementPosition {
  return value === 'before' || value === 'after' || value === 'inside'
}

export function isPlacementPick(value: unknown): value is PlacementPick {
  return isPick(value) && 'position' in value && isPosition(value.position)
}

function isPlacement(value: unknown): value is PlacementOptions {
  return (
    typeof value === 'object' &&
    value !== null &&
    'label' in value &&
    typeof value.label === 'string' &&
    'position' in value &&
    isPosition(value.position)
  )
}

function isPickDetail(value: unknown): value is PickDetail {
  return (
    isPick(value) &&
    'label' in value &&
    typeof value.label === 'string' &&
    'text' in value &&
    typeof value.text === 'string' &&
    'parentSelector' in value &&
    (typeof value.parentSelector === 'string' || value.parentSelector === null) &&
    (!('matchCount' in value) ||
      (typeof value.matchCount === 'number' && Number.isInteger(value.matchCount) && value.matchCount >= 0)) &&
    (!('canInsertInside' in value) || typeof value.canInsertInside === 'boolean')
  )
}

function isHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false
  }
  try {
    const url = new URL(value)
    return (url.protocol === 'https:' || url.protocol === 'http:') && !url.username && !url.password
  } catch {
    return false
  }
}

export function isPickerMessage(value: unknown): value is PickerMessage {
  if (typeof value !== 'object' || value === null || !('type' in value)) {
    return false
  }
  switch (value.type) {
    case 'rm-picker:ready':
      return true
    case 'rm-picker:init':
      return (
        'multiple' in value &&
        typeof value.multiple === 'boolean' &&
        'browse' in value &&
        typeof value.browse === 'boolean' &&
        (!('restrictTo' in value) || value.restrictTo === undefined || typeof value.restrictTo === 'string') &&
        (!('placement' in value) || value.placement === undefined || isPlacement(value.placement)) &&
        (!('scale' in value) ||
          value.scale === undefined ||
          (typeof value.scale === 'number' && Number.isFinite(value.scale) && value.scale > 0)) &&
        'picks' in value &&
        Array.isArray(value.picks) &&
        value.picks.every('placement' in value && value.placement ? isPlacementPick : isPick)
      )
    case 'rm-picker:pick':
      return (
        isPick(value) &&
        typeof value.path === 'string' &&
        typeof value.domain === 'string' &&
        (!('canInsertInside' in value) || typeof value.canInsertInside === 'boolean')
      )
    case 'rm-picker:unpick':
      return isPick(value)
    case 'rm-picker:navigated':
      return 'url' in value && isHttpUrl(value.url) && 'path' in value && typeof value.path === 'string'
    case 'rm-picker:navigate':
      return 'url' in value && isHttpUrl(value.url)
    case 'rm-picker:mode':
      return 'browse' in value && typeof value.browse === 'boolean'
    case 'rm-picker:position':
      return 'position' in value && isPosition(value.position)
    case 'rm-picker:ghost':
      return 'canInsertInside' in value && typeof value.canInsertInside === 'boolean'
    case 'rm-picker:details':
      return 'picks' in value && Array.isArray(value.picks) && value.picks.every(isPickDetail)
    case 'rm-picker:focus':
      return (
        'pick' in value &&
        (value.pick === null || isPick(value.pick)) &&
        'scroll' in value &&
        typeof value.scroll === 'boolean'
      )
    case 'rm-picker:alt':
      return 'held' in value && typeof value.held === 'boolean'
    case 'rm-picker:clear-hover':
    case 'rm-picker:loading':
      return true
    default:
      return false
  }
}

export function acceptsPickerMessage(
  event: Pick<MessageEvent<unknown>, 'origin' | 'source' | 'data'>,
  origin: string,
  source: MessageEventSource | null,
  nonce: string,
  allowProxyRedirect = false
): boolean {
  if (
    source === null ||
    event.source !== source ||
    !isPickerMessage(event.data) ||
    !('nonce' in event.data) ||
    !nonce ||
    event.data.nonce !== nonce
  ) {
    return false
  }
  if (!allowProxyRedirect) {
    return event.origin === origin
  }
  try {
    const expected = new URL(origin)
    const received = new URL(event.origin)
    const proxyDomain = expected.hostname.split('.').slice(1).join('.')
    const suffix = `.${proxyDomain}`
    const label = received.hostname.slice(0, -suffix.length)
    return (
      received.origin === event.origin &&
      received.protocol === expected.protocol &&
      received.port === expected.port &&
      received.hostname.endsWith(suffix) &&
      /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label)
    )
  } catch {
    return false
  }
}

export function splitSelectorList(value: string): string[] {
  const selectors: string[] = []
  let start = 0
  let depth = 0
  let quote = ''
  let escaped = false
  for (let index = 0; index < value.length; index++) {
    const character = value[index]
    if (escaped) {
      escaped = false
      continue
    }
    if (character === '\\') {
      escaped = true
      continue
    }
    if (quote) {
      if (character === quote) {
        quote = ''
      }
      continue
    }
    if (character === '"' || character === "'") {
      quote = character
      continue
    }
    if (character === '[' || character === '(') {
      depth++
    } else if (character === ']' || character === ')') {
      depth--
    } else if (character === ',' && depth === 0) {
      selectors.push(value.slice(start, index).trim())
      start = index + 1
    }
  }
  selectors.push(value.slice(start).trim())
  return [...new Set(selectors.filter(Boolean))]
}

export function joinPickerSelectors(picks: readonly ElementPick[]): string {
  return [...new Set(picks.map((pick) => pick.selector))].join(', ')
}
